'use client'

/* eslint-disable react/hook-use-state */
import { arrayMove } from '@dnd-kit/sortable'
import { noop } from 'lodash'
import { createContext, ReactNode, useCallback, useMemo, useState } from 'react'

import { UiState, useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import { InitialMediaSelectData } from '@marketplace-web/item-upload/item-upload-data'
import {
  ImageOrientation,
  MediaSelectPhotoOrientation,
  MediaUploadPhotoModel,
} from '@marketplace-web/item-upload/media-upload-data'

import { MediaSelectContextType } from '../types/context'

export const MediaSelectContext = createContext<MediaSelectContextType>({
  photos: [],
  photoOrientations: [],
  photoTips: [],
  uiStateWithSetter: [UiState.Idle, noop],
  photosCount: 0,
  photoTempIds: [],
  removePhoto: noop,
  setPhotoOrientation: noop,
  changePhotoOrder: noop,
  addPhotos: noop,
  photoInitializationState: {},
  updatePhotoInitializationState: noop,
  initialPhotosPresent: false,
})

export const MediaSelectContextProvider = ({
  children,
  initialMediaSelectData,
}: {
  children: ReactNode
  initialMediaSelectData?: InitialMediaSelectData
}) => {
  const [photos, setPhotos] = useState<Array<MediaUploadPhotoModel>>(
    initialMediaSelectData?.photos || [],
  )
  const [photoOrientations, setPhotoOrientations] = useState<Array<MediaSelectPhotoOrientation>>(
    initialMediaSelectData?.photoOrientations || [],
  )

  const initialPhotoInitializationState = initialMediaSelectData?.photos?.reduce<
    Record<number, boolean>
  >((acc, currValue) => {
    // eslint-disable-next-line no-param-reassign
    acc[currValue.id] = true

    return acc
  }, {})

  const [photoInitializationState, setPhotoInitializationState] = useState<Record<number, boolean>>(
    initialPhotoInitializationState || {},
  )
  const uiStateWithSetter = useState(UiState.Idle)
  const photoTempIds = photos.reduce<Array<string>>((acc, photo) => {
    if (photo.tempUuid) acc.push(photo.tempUuid)

    return acc
  }, [])

  const addPhotos = useLatestCallback(
    (
      photosToBeAdded: Array<MediaUploadPhotoModel>,
      photoOrientationsToBeAdded: Array<MediaSelectPhotoOrientation>,
    ) => {
      if (!photosToBeAdded.length) return

      setPhotos(prev => [...prev, ...photosToBeAdded])
      setPhotoOrientations(prev => [...prev, ...photoOrientationsToBeAdded])
      setPhotoInitializationState(prev => {
        const newState = { ...prev }
        photosToBeAdded.forEach(photo => {
          newState[photo.id] = false
        })

        return newState
      })
    },
  )

  const removePhoto = (photoId: number) => {
    setPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoId))
    setPhotoOrientations(prev => prev.filter(photo => photo.id !== photoId))
    setPhotoInitializationState(prev => {
      const newState = { ...prev }
      delete newState[photoId]

      return newState
    })
  }

  const changePhotoOrder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    setPhotos(prev => arrayMove(prev, oldIndex, newIndex))
    setPhotoOrientations(prev => arrayMove(prev, oldIndex, newIndex))
  }

  const setPhotoOrientation = (photoOrientation: MediaSelectPhotoOrientation) => {
    setPhotoOrientations(prev =>
      prev.map(photo =>
        photo.id === photoOrientation.id
          ? { ...photo, orientation: photoOrientation.orientation || ImageOrientation.Degree0 }
          : photo,
      ),
    )
  }

  const updatePhotoInitializationState = useCallback((photoId: number, isInitialized: boolean) => {
    setPhotoInitializationState(prev => ({
      ...prev,
      [photoId]: isInitialized,
    }))
  }, [])

  const providerValue = useMemo(
    () => ({
      photos,
      photoOrientations,
      uiStateWithSetter,
      photosCount: photos.length,
      photoTempIds,
      removePhoto,
      addPhotos,
      setPhotoOrientation,
      changePhotoOrder,
      photoTips: initialMediaSelectData?.photoTips || [],
      photoInitializationState,
      updatePhotoInitializationState,
      initialPhotosPresent: !!initialMediaSelectData?.photos?.length,
    }),
    [
      addPhotos,
      photoTempIds,
      photos,
      photoOrientations,
      uiStateWithSetter,
      photoInitializationState,
      updatePhotoInitializationState,
      initialMediaSelectData?.photoTips,
      initialMediaSelectData?.photos,
    ],
  )

  return <MediaSelectContext.Provider value={providerValue}>{children}</MediaSelectContext.Provider>
}
